/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, Download, Wallet as WalletIcon, AlertCircle } from "lucide-react";
import { money, formatDate } from "@/lib/format";

// Canonical categories used by the Expense form, mirrored here for consistent labels.
const CATEGORY_LABELS = {
  education: "Education",
  medical: "Medical",
  clothing: "Clothing",
  food: "Food",
  childcare: "Childcare",
  extracurricular: "Extra-curricular",
  travel: "Travel",
  dental: "Dental",
  other: "Other",
};
const catLabel = (c) => CATEGORY_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Other");

const STATUS_META = {
  outstanding: { label: "Outstanding", className: "border-[#825A41] bg-[#F0E8E3] text-[#4A3A2E]" },
  partial: { label: "Partially paid", className: "border-[#B88A3A] bg-[#FBF0DC] text-[#6F4E1E]" },
  pending_confirmation: { label: "Awaiting confirmation", className: "border-[#4A6FA5] bg-[#E3EAF3] text-[#2E4A73]" },
  paid: { label: "Paid", className: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]" },
  voided: { label: "Voided", className: "border-[#8A8A8A] bg-[#EAEAEA] text-[#6A6A6A]" },
};

export default function ChildExpenses() {
  const { id: childId } = useParams();
  const { user, currency, members } = useAuth();

  const [child, setChild] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);

  // Scope: 'all' | 'month' | 'custom'
  const [scope, setScope] = useState("all");
  const [monthValue, setMonthValue] = useState(new Date().toISOString().slice(0, 7));
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // On-page filters (client-side only; don't need to refetch)
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Quick-filter helpers — apply a preset scope + values in one click.
  const applyQuickRange = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "this-month") {
      setScope("month");
      setMonthValue(today.toISOString().slice(0, 7));
      return;
    }
    if (preset === "last-30") {
      const from = new Date(today); from.setDate(from.getDate() - 29);
      setScope("custom"); setFromDate(iso(from)); setToDate(iso(today));
      return;
    }
    if (preset === "ytd") {
      setScope("custom"); setFromDate(`${today.getFullYear()}-01-01`); setToDate(iso(today));
      return;
    }
    if (preset === "all") {
      setScope("all"); setFromDate(""); setToDate("");
    }
  };

  // Build the query-string fragment that maps the scope selector to backend params.
  const scopeParams = useMemo(() => {
    if (scope === "month") return { month: monthValue };
    if (scope === "custom") return {
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    };
    return {};
  }, [scope, monthValue, fromDate, toDate]);

  // Fetch child profile once.
  useEffect(() => {
    let cancelled = false;
    api.get("/children")
      .then((r) => {
        if (cancelled) return;
        const found = (r.data || []).find((c) => c.id === childId);
        setChild(found || null);
        if (!found) setErr("Child not found or you don't have access.");
      })
      .catch((e) => !cancelled && setErr(e.response?.data?.detail || "Failed to load child."));
    return () => { cancelled = true; };
  }, [childId]);

  // Refetch summary + list whenever scope changes.
  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    const params = { child_id: childId, ...scopeParams };
    Promise.all([
      api.get("/expenses/summary", { params }),
      api.get("/expenses", { params }),
    ]).then(([s, l]) => {
      if (cancelled) return;
      setSummary(s.data);
      setItems(l.data || []);
    }).catch((e) => !cancelled && setErr(e.response?.data?.detail || "Failed to load expenses."));
    return () => { cancelled = true; };
  }, [childId, scopeParams]);

  const nameOf = (uid) => {
    const m = (members || []).find((x) => x.id === uid);
    return m ? m.name : "—";
  };

  const downloadPdf = () => {
    const qs = new URLSearchParams({ child_id: childId });
    if (scope === "month") qs.set("month", monthValue);
    if (scope === "custom") {
      if (fromDate) qs.set("from_date", fromDate);
      if (toDate) qs.set("to_date", toDate);
    }
    const today = new Date().toISOString().slice(0, 10);
    const fname = `child-expenses-${childId.slice(0, 8)}-${today}.pdf`;
    // Iter 124.pdf-hybrid — mirror params to the async endpoint so the
    // background job (if triggered by the 15s / 30s fallback) produces
    // the same PDF the sync path would have.
    const fallbackPayload = { child_id: childId };
    if (scope === "month") fallbackPayload.month = monthValue;
    if (scope === "custom") {
      if (fromDate) fallbackPayload.from_date = fromDate;
      if (toDate) fallbackPayload.to_date = toDate;
    }
    downloadFile(
      `${API}/expenses/export/pdf?${qs.toString()}`,
      fname,
      {
        label: "Generating expenses PDF",
        successMessage: "PDF ready",
        asyncFallback: {
          endpoint: "/exports/expenses/pdf/start",
          payload: fallbackPayload,
        },
      },
    );
  };

  // Derivations — declared BEFORE any early return so hook call order stays stable across renders.
  // Putting these after the `if (!user) return …` branch would cause React 19 to throw
  // "Rendered more hooks than during the previous render" the moment `user` flips to null.
  const categoryEntries = Object.entries(summary?.by_category || {})
    .sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(1, ...categoryEntries.map(([, v]) => v));
  const presentCategories = useMemo(() => {
    const set = new Set();
    (items || []).forEach((x) => x.category && set.add(x.category));
    return [...set].sort();
  }, [items]);
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (items || []).filter((x) => {
      if (catFilter !== "all" && x.category !== catFilter) return false;
      if (statusFilter !== "all") {
        const s = x.voided ? "voided" : (x.status || "outstanding");
        if (statusFilter === "outstanding" && s !== "outstanding") return false;
        if (statusFilter === "partial" && s !== "partial") return false;
        if (statusFilter === "paid" && s !== "paid") return false;
      }
      if (!needle) return true;
      const hay = `${x.title || ""}\n${x.notes || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [items, query, catFilter, statusFilter]);

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <div className="space-y-6" data-testid="child-expenses-page">
      <div>
        <Link to="/app/expenses" className="inline-flex items-center gap-1 text-sm text-[#1F9FA4] hover:text-[#178085]" data-testid="child-expenses-back">
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Expenses
        </Link>
      </div>

      {err && (
        <div className="border border-[#E8B8B2] bg-[#FBEFED] text-[#8C2A22] rounded-sm p-3 text-sm inline-flex items-center gap-2" data-testid="child-expenses-error">
          <AlertCircle className="w-4 h-4" strokeWidth={1.5} /> {err}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {child && (
            <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading text-base shrink-0"
              style={{ backgroundColor: child.color || "#1F9FA4" }} aria-hidden>
              {(child.name || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Expenses for</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium truncate" data-testid="child-expenses-heading">{child?.name || "Child"}</h1>
          </div>
        </div>
        <Button onClick={downloadPdf} variant="outline" className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#D6F0EF] rounded-sm" data-testid="child-expenses-export-button">
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export PDF
        </Button>
      </div>

      {/* Scope selector */}
      <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3" data-testid="child-expenses-scope">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">View</Label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Quick range presets">
            {[
              { v: "all", label: "All time" },
              { v: "this-month", label: "This month" },
              { v: "last-30", label: "Last 30 days" },
              { v: "ytd", label: "Year to date" },
            ].map((q) => (
              <button key={q.v} type="button"
                onClick={() => applyQuickRange(q.v)}
                className="px-2.5 py-1 text-[11px] uppercase tracking-[0.08em] border border-[#D9E1E3] bg-white text-[#6F828A] hover:bg-[#EEF3F4] rounded-sm"
                data-testid={`child-expenses-quick-${q.v}`}>
                {q.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1" role="radiogroup" aria-label="Expense range">
          {[
            { v: "all", label: "All-time" },
            { v: "month", label: "Month" },
            { v: "custom", label: "Custom" },
          ].map((opt) => {
            const active = scope === opt.v;
            return (
              <button key={opt.v} type="button" role="radio" aria-checked={active}
                onClick={() => setScope(opt.v)}
                className={`px-3 py-2 text-sm border rounded-sm transition-colors ${active ? "bg-[#1F9FA4] border-[#1F9FA4] text-white" : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"}`}
                data-testid={`child-expenses-scope-${opt.v}`}>
                {opt.label}
              </button>
            );
          })}
        </div>
        {scope === "month" && (
          <div>
            <Label className="text-xs text-[#6F828A]">Month</Label>
            <Input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="max-w-[220px]" data-testid="child-expenses-month-input" />
          </div>
        )}
        {scope === "custom" && (
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label className="text-xs text-[#6F828A]">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="child-expenses-from-input" />
            </div>
            <div>
              <Label className="text-xs text-[#6F828A]">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="child-expenses-to-input" />
            </div>
          </div>
        )}
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="space-y-4" data-testid="child-expenses-summary">
          <p className="text-xs text-[#6F828A]">
            Totals are proportional — when an expense covers multiple children, the amount is divided across them so sibling totals always reconcile to the household total. Individual entries below show the full original amount with a note of the share attributed to {child?.name || "this child"}.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Total for this child" value={money(summary.total, currency)} testid="child-tile-total" accent="teal" />
            <Tile label="Paid upfront" value={money(summary.total_paid_upfront ?? summary.total, currency)} testid="child-tile-paid-upfront" accent="neutral" />
            <Tile label="Settled" value={money(summary.total_reimbursed ?? summary.paid, currency)} testid="child-tile-settled" accent="green" />
            <Tile label="Outstanding" value={money(summary.net_outstanding ?? summary.outstanding, currency)} testid="child-tile-outstanding" accent={(summary.net_outstanding ?? summary.outstanding) > 0 ? "rose" : "neutral"} />
          </div>

          {/* Per-parent reimbursement breakdown */}
          <div className="grid md:grid-cols-2 gap-3" data-testid="child-per-parent">
            {(members || []).map((m) => {
              const pm = summary.per_member?.[m.id];
              if (!pm) return null;
              const isYou = m.id === user?.id;
              return (
                <div key={m.id} className={`border border-[#D9E1E3] ${isYou ? "bg-[#F2FAFA] border-l-4 border-l-[#1F9FA4]" : "bg-[#F7F3FB] border-l-4 border-l-[#7A5AA6]"} p-4 rounded-md`} data-testid={`child-per-parent-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-sm ${isYou ? "bg-[#1F9FA4] text-white" : "bg-[#7A5AA6] text-white"}`}>
                        {isYou ? "You" : "Co-parent"}
                      </span>
                      <span className="font-heading text-base font-medium truncate">{m.name}</span>
                      {m.is_placeholder && <span className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">Placeholder</span>}
                    </div>
                    <span className={`text-xs font-mono-data ${pm.balance > 0 ? "text-[#1F9FA4]" : pm.balance < 0 ? "text-[#C0291D]" : "text-[#6F828A]"}`}>
                      {pm.balance > 0 ? `+${money(pm.balance, currency)} owed to them` : pm.balance < 0 ? `${money(pm.balance, currency)} they owe` : "Settled"}
                    </span>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <InfoPair label="Paid upfront" value={money(pm.paid_by_them, currency)} />
                    <InfoPair label="Their share" value={money(pm.their_share, currency)} />
                    <InfoPair label="Reimbursed" value={money(pm.reimbursed_to_them, currency)} />
                    <InfoPair label="Still owed" value={money(pm.outstanding_owed, currency)} danger={pm.outstanding_owed > 0} />
                  </dl>
                </div>
              );
            })}
          </div>

          {/* Per-category breakdown */}
          {categoryEntries.length > 0 && (
            <div className="border border-[#D9E1E3] bg-white rounded-md p-4 space-y-3" data-testid="child-by-category">
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Spend by category (proportional)</div>
              {categoryEntries.map(([cat, v]) => {
                const pct = Math.max(2, Math.round((v / maxCat) * 100));
                return (
                  <div key={cat} className="text-sm" data-testid={`child-cat-row-${cat}`}>
                    <div className="flex justify-between items-baseline">
                      <span className="text-[#2E3A3F]">{catLabel(cat)}</span>
                      <span className="font-mono-data tabular-nums text-[#6F828A]">{money(v, currency)}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-sm bg-[#EEF3F4] overflow-hidden">
                      <div className="h-full bg-[#1F9FA4]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Expense list with full amount + child's share badge */}
      <div className="space-y-3" data-testid="child-expense-list">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">Individual entries</h2>
          <span className="text-xs text-[#6F828A] font-mono-data tabular-nums" data-testid="child-expense-count-label">
            {filteredItems.length}{items.length !== filteredItems.length ? ` of ${items.length}` : ""} {items.length === 1 ? "entry" : "entries"}
          </span>
        </div>

        {/* On-page filters (client-side; don't re-hit the backend) */}
        {items.length > 0 && (
          <div className="grid md:grid-cols-3 gap-2" data-testid="child-expense-filters">
            <div className="relative md:col-span-1">
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title or note…"
                className="pr-7"
                data-testid="child-expense-search-input"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6F828A] hover:text-[#2E3A3F] text-sm"
                  data-testid="child-expense-search-clear">×</button>
              )}
            </div>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="border border-[#D9E1E3] rounded-sm bg-white px-2.5 h-9 text-sm text-[#2E3A3F]"
              aria-label="Filter by category"
              data-testid="child-expense-cat-filter"
            >
              <option value="all">All categories</option>
              {presentCategories.map((c) => (
                <option key={c} value={c}>{catLabel(c)}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[#D9E1E3] rounded-sm bg-white px-2.5 h-9 text-sm text-[#2E3A3F]"
              aria-label="Filter by status"
              data-testid="child-expense-status-filter"
            >
              <option value="all">Any status</option>
              <option value="outstanding">Outstanding</option>
              <option value="partial">Partially settled</option>
              <option value="paid">Settled</option>
            </select>
          </div>
        )}

        {items.length === 0 && (
          <div className="border border-[#D9E1E3] bg-white rounded-md p-10 text-center text-[#6F828A]" data-testid="child-expense-empty">
            <WalletIcon className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} /> No expenses for {child?.name || "this child"} in this range.
          </div>
        )}
        {items.length > 0 && filteredItems.length === 0 && (
          <div className="border border-[#D9E1E3] bg-white rounded-md p-6 text-center text-sm text-[#6F828A]" data-testid="child-expense-empty-filtered">
            No entries match your filters.{" "}
            <button type="button" className="underline text-[#1F9FA4] hover:text-[#178085]"
              onClick={() => { setQuery(""); setCatFilter("all"); setStatusFilter("all"); }}
              data-testid="child-expense-clear-filters">Clear filters</button>.
          </div>
        )}
        {filteredItems.map((x) => {
          const status = x.voided ? "voided" : (x.status || "outstanding");
          const meta = STATUS_META[status] || STATUS_META.outstanding;
          const covers = x.covers_children_count || 1;
          const childPortion = x.child_portion ?? Number(x.amount || 0);
          return (
            <div key={x.id} className="border border-[#D9E1E3] bg-white rounded-md p-4" data-testid={`child-expense-row-${x.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-block text-[10px] uppercase tracking-[0.1em] border rounded-sm px-1.5 py-0.5 ${meta.className}`}>{meta.label}</span>
                    {x.all_children && (
                      <span className="text-[10px] uppercase tracking-[0.1em] border border-[#C8E0D4] bg-[#F1F8F4] text-[#1F6A47] rounded-sm px-1.5 py-0.5" data-testid={`expense-all-children-${x.id}`}>All children</span>
                    )}
                    {!x.all_children && covers > 1 && (
                      <span className="text-[10px] uppercase tracking-[0.1em] border border-[#D9E1E3] bg-[#EEF3F4] text-[#4A6170] rounded-sm px-1.5 py-0.5" data-testid={`expense-shared-${x.id}`}>Shared with {covers} children</span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{catLabel(x.category)}</span>
                  </div>
                  <div className="font-heading text-base font-medium mt-1">{x.title}</div>
                  <div className="text-xs text-[#6F828A] mt-0.5">{formatDate(x.date)} · {nameOf(x.paid_by)} paid {money(x.amount, currency)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">{child?.name || "Child"}'s share</div>
                  <div className="font-heading text-lg font-semibold tabular-nums text-[#2E3A3F]" data-testid={`child-share-${x.id}`}>{money(childPortion, currency)}</div>
                  {childPortion < Number(x.amount || 0) && (
                    <div className="text-[10px] text-[#6F828A]">of {money(x.amount, currency)}</div>
                  )}
                </div>
              </div>
              {x.notes && <div className="mt-2 text-sm text-[#4A5459] whitespace-pre-wrap">{x.notes}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Tile = ({ label, value, testid, accent = "teal", plain = false }) => {
  const palette = {
    teal:    { bar: "#1F9FA4", text: "text-[#2E3A3F]" },
    green:   { bar: "#2E8B6A", text: "text-[#2E8B6A]" },
    rose:    { bar: "#C0291D", text: "text-[#C0291D]" },
    neutral: { bar: "#6F828A", text: "text-[#2E3A3F]" },
  }[accent] || { bar: "#1F9FA4", text: "text-[#2E3A3F]" };
  return (
    <div className="relative overflow-hidden border border-[#D9E1E3] bg-white rounded-md px-4 pt-3 pb-4" style={{ borderTop: `3px solid ${palette.bar}` }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">{label}</div>
      <div className={`mt-2 font-heading text-xl font-semibold tabular-nums ${palette.text}`} data-testid={`${testid}-value`}>
        {plain ? value : value}
      </div>
    </div>
  );
};

const InfoPair = ({ label, value, danger = false }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</dt>
    <dd className={`font-mono-data tabular-nums ${danger ? "text-[#C0291D]" : ""}`}>{value}</dd>
  </div>
);
